import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25DndSortableModule } from "../../modules/s25-dnd-sortable/s25.dnd.sortable.module";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { SpreadSheetService } from "../../modules/s25-spreadsheet/spreadsheet.service";
import { s25Resource } from "../../util/s25.resource";
import { S25Util } from "../../util/s25-util";
import { ReportChartComponent } from "./report.chart.component";
import { breakdownToSheetRows, cardsToSheetRows, reportExportFileName } from "./report.export";
import { formatReportNumber } from "./report.chart.options";
import { loadReportDashboardLayouts, normalizeReportLayout, saveReportDashboardLayout } from "./report.layout.prefs";
import {
    ReportBreakdown,
    ReportDrillDownEvent,
    ReportExportEvent,
    ReportLayout,
    ReportMeasureOption,
    ReportSegment,
    ReportSeries,
    ReportSummaryCard,
    ReportValueFormatter,
    ReportWidgetDef,
} from "./report.types";

function mergeVisibleWidgetOrder(layout: ReportLayout, reorderedVisible: string[]): string[] {
    const hiddenKeys = layout.order.filter((widgetKey) => layout.hidden[widgetKey]);
    const visibleKeys = layout.order.filter((widgetKey) => !layout.hidden[widgetKey]);
    const reordered = [...reorderedVisible];
    for (const widgetKey of visibleKeys) {
        if (!reordered.includes(widgetKey)) {
            reordered.push(widgetKey);
        }
    }
    return [...reordered, ...hiddenKeys];
}

@TypeManagerDecorator("s25-ng-report-dashboard")
@Component({
    selector: "s25-ng-report-dashboard",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ReportChartComponent, S25DndSortableModule, S25IconComponent],
    host: { class: "report-dashboard" },
    template: `
        <div class="report-dashboard__shell">
            <ng-content select="[report-filters]" />

            <div class="report-dashboard__widget-toggles">
                @for (widget of widgets(); track widget.key) {
                    <label class="report-dashboard__chip">
                        <input
                            type="checkbox"
                            class="c-input"
                            [checked]="!isHidden(widget.key)"
                            (change)="toggleWidget(widget.key)"
                        />
                        {{ widget.title }}
                    </label>
                }
            </div>

            @if (loading()) {
                <p class="report-dashboard__muted">Loading…</p>
            } @else if (error()) {
                <p class="report-dashboard__error" role="alert">{{ error() }}</p>
            } @else {
                <ng-content select="[report-detail]" />

                <div
                    class="report-dashboard__widgets"
                    s25-ng-dnd-sortable
                    [items]="sortableWidgetKeys()"
                    (orderChanged)="onWidgetOrderChanged($event)"
                >
                    @for (widgetKey of sortableWidgetKeys(); track widgetKey; let widgetIndex = $index) {
                        @if (widgetDefByKey(widgetKey); as widget) {
                            <section
                                class="report-dashboard__widget"
                                s25-ng-dnd-sortable-item
                                [index]="widgetIndex"
                                [draggable]="true"
                            >
                                <header class="report-dashboard__widget-header">
                                    <s25-ng-icon [type]="'dragHandle'" [ariaLabel]="'Reorder ' + widget.title" />
                                    <h3 class="report-dashboard__widget-title">{{ widget.title }}</h3>
                                    <div class="report-dashboard__widget-actions">
                                        <button type="button" class="c-textButton" (click)="exportWidgetData(widget)">
                                            Export
                                        </button>
                                    </div>
                                </header>

                                @if (widget.chartType === "cards") {
                                    <div class="report-dashboard__cards">
                                        @for (card of cards(); track card.label) {
                                            <div class="report-dashboard__card">
                                                <div class="report-dashboard__card-value">{{ card.value }}</div>
                                                <div class="report-dashboard__card-label">{{ card.label }}</div>
                                            </div>
                                        }
                                    </div>
                                } @else {
                                    @if (isOverlapping(widget)) {
                                        <p class="report-dashboard__overlap-note">
                                            Organizations in more than one category appear in multiple rows; totals
                                            double-count.
                                        </p>
                                    }
                                    <s25-ng-report-chart
                                        [breakdown]="breakdownFor(widget)"
                                        [series]="series()"
                                        [measure]="measure()"
                                        [chartType]="widget.chartType"
                                        [title]="widget.title"
                                        [formatValue]="formatValue()"
                                        (segmentClick)="onSegmentClick(widget, $event)"
                                    />
                                }
                            </section>
                        }
                    }
                </div>
            }
        </div>
    `,
    styles: `
        :host {
            display: block;
        }
        .report-dashboard__shell {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }
        .report-dashboard__widget-toggles {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
        }
        .report-dashboard__chip {
            display: inline-flex;
            align-items: center;
            gap: 0.35rem;
            padding: 0.25rem 0.5rem;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            font-size: 0.875rem;
        }
        .report-dashboard__widgets {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
        }
        .report-dashboard__widget {
            border: 1px solid var(--color--border);
            border-radius: 6px;
            padding: 1rem;
            background: var(--color--surface-1-bg, #fff);
        }
        .report-dashboard__widget-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 0.75rem;
        }
        .report-dashboard__widget-title {
            flex: 1;
            margin: 0;
            font-size: 1.1rem;
        }
        .report-dashboard__widget-actions {
            margin-left: auto;
        }
        .report-dashboard__cards {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
            gap: 1rem;
        }
        .report-dashboard__card {
            padding: 0.75rem;
            border-radius: 4px;
            background: var(--color--surface-2-bg, #f5f5f5);
            text-align: center;
        }
        .report-dashboard__card-value {
            font-size: 1.35rem;
            font-weight: 600;
        }
        .report-dashboard__card-label {
            font-size: 0.85rem;
            color: var(--color--text-muted);
        }
        .report-dashboard__muted {
            color: var(--color--text-muted);
        }
        .report-dashboard__error {
            color: var(--color--error-text, #b00020);
        }
        .report-dashboard__overlap-note {
            font-size: 0.875rem;
            color: var(--color--text-muted);
            margin: 0 0 0.5rem;
        }
        .report-dashboard__widget[s25-ng-dnd-sortable-item][draggable="true"] {
            cursor: grab;
        }
    `,
})
export class ReportDashboardComponent {
    readonly dashboardId = input.required<string>();
    readonly title = input("Report");
    readonly widgets = input.required<ReportWidgetDef[]>();
    readonly loading = input(false);
    readonly error = input<string | undefined>();
    readonly series = input<ReportSeries[]>([]);
    readonly measure = input.required<string>();
    readonly measureOptions = input<ReportMeasureOption[]>([]);
    readonly breakdowns = input<ReportBreakdown[]>([]);
    readonly cards = input<ReportSummaryCard[]>([]);
    readonly formatValue = input<ReportValueFormatter>(formatReportNumber);

    readonly drillDown = output<ReportDrillDownEvent>();
    readonly exportWidget = output<ReportExportEvent>();

    readonly layoutPrefRes = s25Resource({
        loader: () => loadReportDashboardLayouts(),
    });

    readonly savedLayout = computed(() => {
        const layouts = this.layoutPrefRes.value();
        if (!layouts) {
            return undefined;
        }
        return layouts[this.dashboardId()];
    });

    readonly layout = linkedSignal(() => normalizeReportLayout(this.savedLayout(), this.widgets()));

    readonly sortableWidgetKeys = linkedSignal(() =>
        this.layout().order.filter((widgetKey) => !this.layout().hidden[widgetKey]),
    );

    private readonly widgetByKey = computed(() => {
        const map = new Map<string, ReportWidgetDef>();
        for (const widget of this.widgets()) {
            map.set(widget.key, widget);
        }
        return map;
    });

    private readonly breakdownByDimension = computed(() => {
        const map = new Map<string, ReportBreakdown>();
        for (const breakdown of this.breakdowns()) {
            map.set(breakdown.dimension, breakdown);
        }
        return map;
    });

    widgetDefByKey(widgetKey: string): ReportWidgetDef | undefined {
        return this.widgetByKey().get(widgetKey);
    }

    isHidden(widgetKey: string): boolean {
        return !!this.layout().hidden[widgetKey];
    }

    breakdownFor(widget: ReportWidgetDef): ReportBreakdown | undefined {
        const dimension = widget.dimension;
        if (!dimension) {
            return undefined;
        }
        return this.breakdownByDimension().get(dimension);
    }

    isOverlapping(widget: ReportWidgetDef): boolean {
        const breakdown = this.breakdownFor(widget);
        return !!breakdown?.overlapping;
    }

    toggleWidget(widgetKey: string): void {
        const nextHidden = { ...this.layout().hidden, [widgetKey]: !this.layout().hidden[widgetKey] };
        this.commitLayout({ ...this.layout(), hidden: nextHidden });
    }

    onWidgetOrderChanged(reorderedVisible: string[]): void {
        const order = mergeVisibleWidgetOrder(this.layout(), reorderedVisible);
        this.commitLayout({ ...this.layout(), order });
    }

    onSegmentClick(widget: ReportWidgetDef, segment: ReportSegment): void {
        const dimension = widget.dimension;
        if (!dimension) {
            return;
        }
        const row = this.breakdownFor(widget)?.rows.find(
            (breakdownRow) =>
                breakdownRow.key === segment.rowKey &&
                (!segment.seriesKey || breakdownRow.seriesKey === segment.seriesKey),
        );
        this.drillDown.emit({
            widgetKey: widget.key,
            dimension,
            rowKey: segment.rowKey,
            seriesKey: segment.seriesKey,
            rowLabel: row?.label ?? segment.rowKey,
        });
    }

    async exportWidgetData(widget: ReportWidgetDef): Promise<void> {
        this.exportWidget.emit({ widgetKey: widget.key });
        const fileName = reportExportFileName(this.title(), widget.title);
        try {
            if (widget.chartType === "cards") {
                await SpreadSheetService.downloadExcel(cardsToSheetRows(this.cards()), fileName, widget.title);
                return;
            }
            const breakdown = widget.dimension ? this.breakdownByDimension().get(widget.dimension) : undefined;
            const rows = breakdownToSheetRows(breakdown, widget.title, this.series(), this.measureOptions());
            await SpreadSheetService.downloadExcel(rows, fileName, widget.title);
        } catch (caughtError) {
            S25Util.showError(caughtError, "Failed to export report");
        }
    }

    private commitLayout(layout: ReportLayout): void {
        const normalized = normalizeReportLayout(layout, this.widgets());
        this.layout.set(normalized);
        this.sortableWidgetKeys.set(normalized.order.filter((widgetKey) => !normalized.hidden[widgetKey]));
        saveReportDashboardLayout(this.dashboardId(), normalized).catch((caughtError) =>
            S25Util.showError(caughtError, "Failed to save layout"),
        );
    }
}
